<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import UisAPI from 'dashboard/api/uis';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      apiKey: '',
      virtualPhoneNumber: '',
      checks: [],
      isChecking: false,
      checkFailed: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    // Creating an inbox with a key UIS rejects leaves a dead channel behind,
    // so the connection has to pass before we let the operator continue.
    isConnectionVerified() {
      return (
        this.checks.length > 0 && !this.checks.some(c => c.status === 'error')
      );
    },
    showKeyHint() {
      return this.checks.some(
        c => c.key === 'data_api' && c.status === 'error'
      );
    },
  },
  validations: {
    inboxName: { required },
    apiKey: { required },
    virtualPhoneNumber: { required },
  },
  methods: {
    statusClass(status) {
      if (status === 'ok') return 'text-n-teal-11';
      if (status === 'error') return 'text-n-ruby-11';
      return 'text-n-amber-11';
    },
    checkTitle(key) {
      return this.$t(`INBOX_MGMT.ADD.UIS_CHANNEL.CHECKS.${key.toUpperCase()}`);
    },
    async checkConnection() {
      if (!this.apiKey.trim()) return;
      this.isChecking = true;
      this.checks = [];
      this.checkFailed = false;
      try {
        const { data } = await UisAPI.validateKey(this.apiKey.trim());
        this.checks = data.checks || [];
      } catch (error) {
        this.checkFailed = true;
        useAlert(this.$t('INBOX_MGMT.ADD.UIS_CHANNEL.CHECK_ERROR'));
      } finally {
        this.isChecking = false;
      }
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid || !this.isConnectionVerified) return;

      try {
        const payload = await this.$store.dispatch('inboxes/createChannel', {
          name: this.inboxName.trim(),
          channel: {
            type: 'uis',
            api_key: this.apiKey.trim(),
            virtual_phone_number: this.virtualPhoneNumber.trim(),
          },
        });
        useAlert(this.$t('INBOX_MGMT.ADD.UIS_CHANNEL.ACTIVATED'));
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: payload.id },
        });
      } catch (error) {
        useAlert(
          error?.message || this.$t('INBOX_MGMT.ADD.UIS_CHANNEL.CREATE_ERROR')
        );
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.UIS_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.UIS_CHANNEL.DESC')"
    />
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel">
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$?.inboxName?.$error }">
          {{ $t('INBOX_MGMT.ADD.UIS_CHANNEL.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.UIS_CHANNEL.INBOX_NAME.PLACEHOLDER')
            "
            @blur="v$?.inboxName?.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$?.apiKey?.$error }">
          {{ $t('INBOX_MGMT.ADD.UIS_CHANNEL.API_KEY.LABEL') }}
          <input
            v-model="apiKey"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.UIS_CHANNEL.API_KEY.PLACEHOLDER')"
            @blur="v$?.apiKey?.$touch"
          />
        </label>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.UIS_CHANNEL.API_KEY.HINT') }}
        </p>
        <NextButton
          type="button"
          faded
          blue
          sm
          class="mt-2"
          :is-loading="isChecking"
          :label="$t('INBOX_MGMT.ADD.UIS_CHANNEL.CHECK_BUTTON')"
          @click="checkConnection"
        />
      </div>

      <ul v-if="checks.length" class="mt-4 mb-0 list-none pl-0">
        <li
          v-for="check in checks"
          :key="check.key"
          class="flex items-baseline gap-2 py-1 text-sm"
        >
          <span :class="statusClass(check.status)">●</span>
          <span>{{ checkTitle(check.key) }}</span>
          <span class="text-n-slate-11">— {{ check.message }}</span>
        </li>
      </ul>

      <div
        v-if="showKeyHint"
        class="mt-3 p-3 rounded-lg bg-n-alpha-2 text-xs text-n-slate-11 leading-relaxed"
      >
        <p class="font-medium text-n-slate-12 mb-1">
          {{ $t('INBOX_MGMT.ADD.UIS_CHANNEL.KEY_HELP.TITLE') }}
        </p>
        <p class="mb-1">{{ $t('INBOX_MGMT.ADD.UIS_CHANNEL.KEY_HELP.STEP_1') }}</p>
        <p class="mb-1">{{ $t('INBOX_MGMT.ADD.UIS_CHANNEL.KEY_HELP.STEP_2') }}</p>
        <p class="mb-1">{{ $t('INBOX_MGMT.ADD.UIS_CHANNEL.KEY_HELP.STEP_3') }}</p>
        <p>{{ $t('INBOX_MGMT.ADD.UIS_CHANNEL.KEY_HELP.STEP_4') }}</p>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$?.virtualPhoneNumber?.$error }">
          {{ $t('INBOX_MGMT.ADD.UIS_CHANNEL.VIRTUAL_NUMBER.LABEL') }}
          <input
            v-model="virtualPhoneNumber"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.UIS_CHANNEL.VIRTUAL_NUMBER.PLACEHOLDER')
            "
            @blur="v$?.virtualPhoneNumber?.$touch"
          />
        </label>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.UIS_CHANNEL.VIRTUAL_NUMBER.HINT') }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="!isConnectionVerified"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.UIS_CHANNEL.SUBMIT_BUTTON')"
        />
        <p v-if="!isConnectionVerified" class="mt-2 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.UIS_CHANNEL.VERIFY_FIRST') }}
        </p>
      </div>
    </form>
  </div>
</template>
